import { Button } from "@/components/flexkit/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/podkit/dialog/Dialog";
import { Text } from "@/components/podkit/typography/Text";
import { useRunnerUsage, type RunnerWithUsage } from "@/hooks/use-canonical-domain-runners";
import { pluralizeWithCount } from "@/utils/strings";
import { IconGrid } from "@/assets/icons/geist/IconGrid";
import { IconCode } from "@/assets/icons/geist/IconCode";
import { TrackLocations } from "@/hooks/use-segment";
import { type FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";

type EnforceCustomDomainConfirmationDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    runners: RunnerWithUsage[];
    canonicalDomain: string;
    isLoading?: boolean;
};

export const EnforceCustomDomainConfirmationDialog: FC<EnforceCustomDomainConfirmationDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    runners,
    canonicalDomain,
    isLoading = false,
}) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent data-track-location={TrackLocations.EnforceCustomDomainConfirmationDialog}>
                <DialogHeader>
                    <h2 className="text-lg font-semibold">Confirm Custom Domain Enforcement</h2>
                    <div className="mt-2 flex gap-2">
                        <span className="text-content-warning">⚠️</span>
                        <p className="text-sm text-content-secondary">
                            Enabling this will prevent users from accessing your organization via {canonicalDomain}.{" "}
                            {runners.length === 0 ? (
                                <>No runners are currently using {canonicalDomain}. You can safely enable enforcement.</>
                            ) : (
                                <>
                                    {pluralizeWithCount(runners.length, false, "runner")} registered with{" "}
                                    {canonicalDomain} will become unusable.
                                </>
                            )}
                        </p>
                    </div>
                </DialogHeader>

                <div className="flex flex-col gap-4">

                    {runners.length > 0 && (
                        <>
                            {/* List of affected runners */}
                            <div>
                                <Text className="mb-3 text-sm font-semibold text-content-primary">
                                    Affected Runners
                                </Text>
                                <div className="max-h-64 overflow-y-auto">
                                    <div className="flex flex-col gap-2">
                                        {runners.map((runnerWithUsage) => (
                                            <RunnerUsageCard key={runnerWithUsage.runner.runnerId} {...runnerWithUsage} />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-lg bg-surface-secondary p-4">
                                <Text className="text-sm text-content-secondary">
                                    <strong>Recommendation:</strong> Delete these environments and update project
                                    settings to use runners registered with your custom domain.
                                </Text>
                            </div>
                        </>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        disabled={isLoading}
                        data-tracking-id="enforce-custom-domain-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        loading={isLoading}
                        data-tracking-id="enforce-custom-domain-confirm"
                    >
                        Enable Enforcement
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const RunnerUsageCard: FC<RunnerWithUsage> = ({ runner, projectCount, environmentCount, hasMoreEnvironments }) => {
    const navigate = useNavigate();

    // Fetch real-time usage stats
    const {
        projectCount: liveProjectCount,
        environmentCount: liveEnvironmentCount,
        hasMoreEnvironments: liveHasMore,
        isLoading,
    } = useRunnerUsage(runner.runnerId);

    // Use live data if available, otherwise use passed props
    const displayProjectCount = isLoading ? projectCount : liveProjectCount;
    const displayEnvironmentCount = isLoading ? environmentCount : liveEnvironmentCount;
    const displayHasMore = isLoading ? hasMoreEnvironments : liveHasMore;

    // Generate navigation URLs
    const projectsUrl = useMemo(() => `/projects?runnerId=${runner.runnerId}`, [runner.runnerId]);
    const environmentsUrl = useMemo(() => `/settings/environments?runner=${runner.runnerId}`, [runner.runnerId]);
    const runnerDetailsUrl = useMemo(() => `/settings/runners/${runner.runnerId}`, [runner.runnerId]);

    return (
        <div className="flex items-center justify-between rounded-lg border border-border-primary bg-surface-primary p-3">
            <button
                onClick={() => navigate(runnerDetailsUrl)}
                className="flex flex-col gap-1 text-left transition-opacity hover:opacity-70"
                data-tracking-id="enforce-custom-domain-runner-details"
            >
                <Text className="text-sm font-semibold text-content-primary">{runner.name || "Unnamed Runner"}</Text>
                <Text className="text-xs text-content-tertiary">ID: {runner.runnerId}</Text>
            </button>
            <div className="flex gap-3">
                <button
                    onClick={() => navigate(projectsUrl)}
                    disabled={displayProjectCount === 0 || isLoading}
                    className="flex items-center gap-1.5 rounded px-2 py-1 transition-colors hover:bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
                    data-tracking-id="enforce-custom-domain-runner-projects"
                >
                    <IconGrid size="sm" className="text-content-secondary" />
                    <Text className="text-sm text-content-secondary">
                        {isLoading ? "..." : pluralizeWithCount(displayProjectCount, false, "project")}
                    </Text>
                </button>
                <button
                    onClick={() => navigate(environmentsUrl)}
                    disabled={displayEnvironmentCount === 0 || isLoading}
                    className="flex items-center gap-1.5 rounded px-2 py-1 transition-colors hover:bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
                    data-tracking-id="enforce-custom-domain-runner-environments"
                >
                    <IconCode size="sm" className="text-content-secondary" />
                    <Text className="text-sm text-content-secondary">
                        {isLoading ? "..." : pluralizeWithCount(displayEnvironmentCount, displayHasMore, "env")}
                    </Text>
                </button>
            </div>
        </div>
    );
};
